/**
 * H.264 / H.265 Annex B helpers for writing playback frames into an MP4 file.
 *
 * The playback stream delivers one access unit per multipart part, in Annex B
 * form (start-code delimited, parameter sets in-band). MP4 (avc1 / hvc1) wants
 * length-prefixed NAL units with the parameter sets moved into avcC / hvcC.
 */

const H264_NAL = { IDR: 5, SPS: 7, PPS: 8, AUD: 9 };
const H265_NAL = { IRAP_MIN: 16, IRAP_MAX: 21, VPS: 32, SPS: 33, PPS: 34, AUD: 35 };
const H264_HIGH_PROFILES = new Set([100, 110, 122, 244, 44, 83, 86, 118, 128, 138, 139, 134, 135]);

export function splitAnnexB(data) {
  const units = [];
  let start = -1;
  let index = 0;
  while (index + 2 < data.length) {
    if (data[index] === 0 && data[index + 1] === 0 && data[index + 2] === 1) {
      if (start >= 0) units.push(trimTrailingZeros(data, start, index));
      index += 3;
      start = index;
    } else {
      index += 1;
    }
  }
  if (start >= 0 && start < data.length) units.push(trimTrailingZeros(data, start, data.length));
  return units.filter((unit) => unit.length > 0);
}

function trimTrailingZeros(data, start, end) {
  let last = end;
  while (last > start && data[last - 1] === 0) last -= 1;
  return data.subarray(start, last);
}

/** Strips emulation-prevention bytes (00 00 03 → 00 00). */
export function toRbsp(nal) {
  const output = new Uint8Array(nal.length);
  let length = 0;
  let zeros = 0;
  for (let index = 0; index < nal.length; index += 1) {
    const byte = nal[index];
    if (zeros >= 2 && byte === 3) {
      zeros = 0;
      continue;
    }
    output[length] = byte;
    length += 1;
    zeros = byte === 0 ? zeros + 1 : 0;
  }
  return output.subarray(0, length);
}

class BitReader {
  constructor(bytes) {
    this.bytes = bytes;
    this.position = 0;
  }

  bit() {
    const byte = this.bytes[this.position >> 3];
    if (byte === undefined) throw new Error("SPS ended unexpectedly");
    const value = (byte >> (7 - (this.position & 7))) & 1;
    this.position += 1;
    return value;
  }

  bits(count) {
    let value = 0;
    for (let index = 0; index < count; index += 1) value = (value * 2) + this.bit();
    return value;
  }

  skip(count) {
    this.position += count;
  }

  ue() {
    let leadingZeros = 0;
    while (this.bit() === 0) {
      leadingZeros += 1;
      if (leadingZeros > 31) throw new Error("invalid Exp-Golomb code");
    }
    return (2 ** leadingZeros) - 1 + this.bits(leadingZeros);
  }

  se() {
    const value = this.ue();
    return value % 2 === 0 ? -(value / 2) : (value + 1) / 2;
  }
}

function skipH264ScalingList(reader, size) {
  let last = 8;
  let next = 8;
  for (let index = 0; index < size; index += 1) {
    if (next !== 0) next = (last + reader.se() + 256) % 256;
    last = next === 0 ? last : next;
  }
}

export function parseH264Sps(nal) {
  const rbsp = toRbsp(nal);
  const reader = new BitReader(rbsp);
  reader.skip(8);
  const profileIdc = reader.bits(8);
  const constraintFlags = reader.bits(8);
  const levelIdc = reader.bits(8);
  reader.ue();
  let chromaFormatIdc = 1;
  let bitDepthLumaMinus8 = 0;
  let bitDepthChromaMinus8 = 0;
  if (H264_HIGH_PROFILES.has(profileIdc)) {
    chromaFormatIdc = reader.ue();
    if (chromaFormatIdc === 3) reader.skip(1);
    bitDepthLumaMinus8 = reader.ue();
    bitDepthChromaMinus8 = reader.ue();
    reader.skip(1);
    if (reader.bit()) {
      const lists = chromaFormatIdc === 3 ? 12 : 8;
      for (let index = 0; index < lists; index += 1) {
        if (reader.bit()) skipH264ScalingList(reader, index < 6 ? 16 : 64);
      }
    }
  }
  reader.ue();
  const picOrderCntType = reader.ue();
  if (picOrderCntType === 0) {
    reader.ue();
  } else if (picOrderCntType === 1) {
    reader.skip(1);
    reader.se();
    reader.se();
    const cycle = reader.ue();
    for (let index = 0; index < cycle; index += 1) reader.se();
  }
  reader.ue();
  reader.skip(1);
  const widthInMbs = reader.ue() + 1;
  const heightInMapUnits = reader.ue() + 1;
  const frameMbsOnly = reader.bit();
  if (!frameMbsOnly) reader.skip(1);
  reader.skip(1);
  let crop = [0, 0, 0, 0];
  if (reader.bit()) crop = [reader.ue(), reader.ue(), reader.ue(), reader.ue()];

  const subWidthC = chromaFormatIdc === 3 ? 1 : 2;
  const subHeightC = chromaFormatIdc === 1 ? 2 : 1;
  const cropUnitX = chromaFormatIdc === 0 ? 1 : subWidthC;
  const cropUnitY = (chromaFormatIdc === 0 ? 1 : subHeightC) * (2 - frameMbsOnly);
  return {
    width: (widthInMbs * 16) - (cropUnitX * (crop[0] + crop[1])),
    height: ((2 - frameMbsOnly) * heightInMapUnits * 16) - (cropUnitY * (crop[2] + crop[3])),
    profileIdc,
    constraintFlags,
    levelIdc,
    chromaFormatIdc,
    bitDepthLumaMinus8,
    bitDepthChromaMinus8,
  };
}

export function parseH265Sps(nal) {
  const rbsp = toRbsp(nal);
  const reader = new BitReader(rbsp);
  reader.skip(16);
  reader.skip(4);
  const maxSubLayersMinus1 = reader.bits(3);
  const temporalIdNesting = reader.bit();
  const generalProfileTierLevel = rbsp.slice(3, 15);
  reader.skip(96);
  const subLayerProfilePresent = [];
  const subLayerLevelPresent = [];
  for (let index = 0; index < maxSubLayersMinus1; index += 1) {
    subLayerProfilePresent.push(reader.bit());
    subLayerLevelPresent.push(reader.bit());
  }
  if (maxSubLayersMinus1 > 0) {
    for (let index = maxSubLayersMinus1; index < 8; index += 1) reader.skip(2);
  }
  for (let index = 0; index < maxSubLayersMinus1; index += 1) {
    if (subLayerProfilePresent[index]) reader.skip(88);
    if (subLayerLevelPresent[index]) reader.skip(8);
  }
  reader.ue();
  const chromaFormatIdc = reader.ue();
  if (chromaFormatIdc === 3) reader.skip(1);
  let width = reader.ue();
  let height = reader.ue();
  if (reader.bit()) {
    const subWidthC = chromaFormatIdc === 1 || chromaFormatIdc === 2 ? 2 : 1;
    const subHeightC = chromaFormatIdc === 1 ? 2 : 1;
    const [left, right, top, bottom] = [reader.ue(), reader.ue(), reader.ue(), reader.ue()];
    width -= subWidthC * (left + right);
    height -= subHeightC * (top + bottom);
  }
  return {
    width,
    height,
    chromaFormatIdc,
    bitDepthLumaMinus8: reader.ue(),
    bitDepthChromaMinus8: reader.ue(),
    maxSubLayersMinus1,
    temporalIdNesting,
    generalProfileTierLevel,
  };
}

function concatBytes(parts) {
  const length = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(length);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
}

function u16(value) {
  return Uint8Array.of((value >> 8) & 0xff, value & 0xff);
}

function lengthPrefixed(units) {
  return concatBytes(units.flatMap((unit) => [
    Uint8Array.of((unit.length >>> 24) & 0xff, (unit.length >>> 16) & 0xff, (unit.length >>> 8) & 0xff, unit.length & 0xff),
    unit,
  ]));
}

export function buildAvcC({ sps, pps }) {
  const info = parseH264Sps(sps[0]);
  const parts = [
    Uint8Array.of(1, sps[0][1], sps[0][2], sps[0][3], 0xff, 0xe0 | sps.length),
    ...sps.flatMap((unit) => [u16(unit.length), unit]),
    Uint8Array.of(pps.length),
    ...pps.flatMap((unit) => [u16(unit.length), unit]),
  ];
  if (H264_HIGH_PROFILES.has(info.profileIdc)) {
    parts.push(Uint8Array.of(
      0xfc | info.chromaFormatIdc,
      0xf8 | info.bitDepthLumaMinus8,
      0xf8 | info.bitDepthChromaMinus8,
      0,
    ));
  }
  return concatBytes(parts);
}

export function buildHvcC({ vps, sps, pps }) {
  const info = parseH265Sps(sps[0]);
  const arrays = [[H265_NAL.VPS, vps], [H265_NAL.SPS, sps], [H265_NAL.PPS, pps]]
    .filter(([, units]) => units.length > 0);
  return concatBytes([
    Uint8Array.of(1),
    info.generalProfileTierLevel,
    Uint8Array.of(
      0xf0, 0x00,
      0xfc,
      0xfc | info.chromaFormatIdc,
      0xf8 | info.bitDepthLumaMinus8,
      0xf8 | info.bitDepthChromaMinus8,
      0x00, 0x00,
      ((info.maxSubLayersMinus1 + 1) << 3) | (info.temporalIdNesting << 2) | 3,
      arrays.length,
    ),
    ...arrays.flatMap(([type, units]) => [
      Uint8Array.of(0x80 | type),
      u16(units.length),
      ...units.flatMap((unit) => [u16(unit.length), unit]),
    ]),
  ]);
}

/**
 * Splits one Annex B access unit into its MP4 sample (length-prefixed, without
 * parameter sets or access unit delimiters) and any in-band parameter sets.
 */
export function analyzeVideoFrame(videoCodec, data) {
  const isHevc = videoCodec === "H265";
  const params = { vps: [], sps: [], pps: [] };
  const sampleUnits = [];
  let key = false;
  for (const unit of splitAnnexB(data)) {
    if (isHevc) {
      const type = (unit[0] >> 1) & 0x3f;
      if (type === H265_NAL.VPS) params.vps.push(unit);
      else if (type === H265_NAL.SPS) params.sps.push(unit);
      else if (type === H265_NAL.PPS) params.pps.push(unit);
      else if (type !== H265_NAL.AUD) {
        if (type >= H265_NAL.IRAP_MIN && type <= H265_NAL.IRAP_MAX) key = true;
        sampleUnits.push(unit);
      }
    } else {
      const type = unit[0] & 0x1f;
      if (type === H264_NAL.SPS) params.sps.push(unit);
      else if (type === H264_NAL.PPS) params.pps.push(unit);
      else if (type !== H264_NAL.AUD) {
        if (type === H264_NAL.IDR) key = true;
        sampleUnits.push(unit);
      }
    }
  }
  return {
    key,
    params,
    sample: sampleUnits.length ? lengthPrefixed(sampleUnits) : null,
  };
}

function hex(value) {
  return value.toString(16).padStart(2, "0");
}

/** mp4-muxer track options plus the decoder config for the first sample. */
export function buildVideoTrackConfig(videoCodec, params) {
  if (videoCodec === "H265") {
    if (!params.sps.length || !params.pps.length) return null;
    const info = parseH265Sps(params.sps[0]);
    return {
      codec: "hevc",
      width: info.width,
      height: info.height,
      decoderConfig: {
        codec: "hvc1",
        codedWidth: info.width,
        codedHeight: info.height,
        description: buildHvcC(params),
      },
    };
  }
  if (!params.sps.length || !params.pps.length) return null;
  const info = parseH264Sps(params.sps[0]);
  return {
    codec: "avc",
    width: info.width,
    height: info.height,
    decoderConfig: {
      codec: `avc1.${hex(info.profileIdc)}${hex(info.constraintFlags)}${hex(info.levelIdc)}`,
      codedWidth: info.width,
      codedHeight: info.height,
      description: buildAvcC(params),
    },
  };
}
